/**
 * Top-bar geography selector state.
 *
 * - Value: `'all'` or a specific geography id.
 * - Persistence: sticky across sessions (localStorage).
 * - Seeding: on first-ever load, seeded from the user's profile geography;
 *   after that, user edits in the top bar win and the profile no longer moves it.
 *
 * The "show only unreviewed" toggle RESETS whenever the geo selection changes
 * — that behaviour lives here so both consumers (TopBar + stream page) can't
 * fall out of sync.
 */

import { useCallback, useEffect } from 'react';

import { GEO_ALL } from '../types';
import { usePersistentState } from './storage';

const GEO_KEY = 'intake.ui.geoFilter.v1';
const UNREVIEWED_KEY = 'intake.ui.unreviewedOnly.v1';

export function useGeoFilter(profileGeo: string | undefined): {
  geo: string;
  setGeo: (next: string) => void;
  unreviewedOnly: boolean;
  setUnreviewedOnly: (next: boolean) => void;
} {
  const [geo, setGeoRaw] = usePersistentState<string | null>(GEO_KEY, null);
  const [unreviewedOnly, setUnreviewedOnlyRaw] = usePersistentState<boolean>(
    UNREVIEWED_KEY,
    false,
  );

  // Seed geo from the profile exactly once — only when no prior value exists.
  useEffect(() => {
    if (geo === null && profileGeo) {
      setGeoRaw(profileGeo);
    }
  }, [geo, profileGeo, setGeoRaw]);

  const setGeo = useCallback(
    (next: string) => {
      setGeoRaw(next);
      // Per spec: changing geography resets the "unreviewed only" toggle.
      setUnreviewedOnlyRaw(false);
    },
    [setGeoRaw, setUnreviewedOnlyRaw],
  );

  const setUnreviewedOnly = useCallback(
    (next: boolean) => setUnreviewedOnlyRaw(next),
    [setUnreviewedOnlyRaw],
  );

  return {
    geo: geo ?? GEO_ALL,
    setGeo,
    unreviewedOnly,
    setUnreviewedOnly,
  };
}
